<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[3, 18]" :mobile-default-grid="[1, 14]">
      <article class="c-story-card__inner">
        <div v-if="hasMedia" class="c-story-card__media">
          <AdaptiveMedia
            class="c-story-card__adaptive-media"
            :field="mediaField"
            :show-play-button="true"
            :autoplay="true"
            :loop="true"
            :controls="true"
            :has-controls="true"
            :show-image-redius="false"
          />
        </div>

        <div class="c-story-card__content">
          <RichText v-if="fields.title?.value" class="c-story-card__title" :field="fields.title" />
          <RichText
            v-if="fields.description?.value"
            class="c-story-card__description"
            :field="fields.description"
          />
        </div>
      </article>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import AdaptiveMedia from '@/elements/AdaptiveMedia.vue';
import type { StoryCardProps } from '@/types/components/StoryCard';
import utils from '@/utils';

const props = defineProps<StoryCardProps>();
const fields = computed(() => props.fields);
const mediaField = computed(() => fields.value?.image);
const hasMedia = computed(() => {
  const mediaFields = mediaField.value?.fields;

  return !!(
    mediaFields?.desktopImage?.value?.src ||
    mediaFields?.mobileImage?.value?.src ||
    mediaFields?.desktopVideo?.value?.src ||
    mediaFields?.desktopVideo?.value?.href ||
    mediaFields?.mobileVideo?.value?.src ||
    mediaFields?.mobileVideo?.value?.href
  );
});

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-story-card'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-story-card {
  padding: 40px 0;
  background: $white;

  &__inner {
    max-width: 1440px;
    margin: 0 auto;
  }

  &__media {
    overflow: hidden;
    border-radius: 6px;
    background: #eef1f4;
  }

  &__adaptive-media {
    display: block;
    width: 100%;

    .e-adaptive-media__image,
    .e-adaptive-media__video,
    .e-video-player,
    video {
      width: 100%;
      height: auto;
    }

    .e-video-player {
      aspect-ratio: var(--ratio);
    }

    img {
      display: block;
    }
  }

  &__content {
    margin-top: 36px;
  }

  &__title {
    margin: 0;
    color: #404040;
    font-size: 16px;
    line-height: 1.35;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__description {
    margin-top: 2px;
    color: #4f4f4f;
    font-size: 16px;
    line-height: 1.34;
    font-weight: 400;

    p {
      margin: 0;
    }

    p + p {
      margin-top: 0;
    }
  }

  @include tablet {
    &__media {
      border-radius: 6px;
    }

    &__content {
      margin-top: 54px;
    }

    &__title {
      font-size: 16px;
      line-height: 1.25;
    }

    &__description {
      font-size: 16px;
      line-height: 1.35;
    }
  }

  @include tablet-landscape {
    &__content {
      margin-top: 58px;
    }
  }
}
</style>
